import { CommonModule } from "@angular/common";
import {
    Component,
    effect,
    inject,
    OnInit,
    signal,
    ViewChild
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute } from "@angular/router";
import { NandaChatAccountingComponent } from "@app/shared/components/nanda-chat-accounting/nanda-chat-accounting.component";
import { RequestProductGrowthComponent } from "@app/shared/components/request-product-growth/request-product-growth.component";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { AccountingPlanService } from "@app/shared/services/accounting-plan.service";
import { ChatNandaService } from "@app/shared/services/chat-nanda.service";
import { ContactService } from "@app/shared/services/contact.service";
import { DeviceService } from "@app/shared/services/device.service";
import { ReportNotesNavigationComponent } from "@app/views/auth/report/components/report-notes-navigation/report-notes-navigation.component";
import { ReportDashboardComponent } from "@app/views/auth/report/dashboard/report-dashboard.component";
import { EventsService } from "@arch/domain/application/services/events.service";
import { CompanyEntity, ReportEntity } from "@arch/domain/entity";
import {
    GetReportController,
    GetUsersContactController,
    GetUsersContactControllerType,
    ShareReportController,
    ShareReportEventsController,
    UpdateFeatureFlagUserController
} from "@arch/presentation/controllers";
import { environment } from "@environments/environment";
import { ReportBalanceComponent } from "../balance/report-balance.component";
import { ReportBudgetComponent } from "../budget/report-budget.component";
import { ReportDreComponent } from "../dre/report-dre.component";
import { ReportProjectionComponent } from "../projection/report-projection.component";
import { DashNewsDialogComponent } from "./dash-news/dash-news-dialog.component";
import { EditWhatsappDialogComponent } from "./edit-whatsapp/edit-whatsapp-dialog.component";
import { ShareEmailDialogComponent } from "./share-email-dialog/share-email-dialog.component";
import { ShareWppDialogComponent } from "./share-wpp-dialog/share-wpp-dialog.component";

@Component({
    selector: "app-report-preview",
    imports: [
        CommonModule,
        SharedDsnModule,
        SharedMaterialModule,
        ReportNotesNavigationComponent,
        ReportDashboardComponent,
        ReportDreComponent,
        ReportBalanceComponent,
        ReportBudgetComponent,
        ReportProjectionComponent,
        NandaChatAccountingComponent
    ],
    templateUrl: "./report-preview.component.html",
    styleUrl: "./report-preview.component.scss"
})
export class ReportPreviewComponent implements OnInit {
    private readonly snackBar = inject(MatSnackBar);
    private readonly deviceService = inject(DeviceService);
    private readonly dialog = inject(MatDialog);
    private readonly contactService = inject(ContactService);
    private readonly shareReportController = inject(ShareReportController);
    private readonly route = inject(ActivatedRoute);
    private readonly eventsService = inject(EventsService);
    public readonly chatNandaService = inject(ChatNandaService);
    public readonly accountingPlanService = inject(AccountingPlanService);
    private readonly updateFeatureFlagUserController = inject(
        UpdateFeatureFlagUserController
    );
    private readonly getUsersContactController = inject(
        GetUsersContactController
    );
    private readonly getReportController = inject(GetReportController);
    private readonly shareReportEventsController = inject(
        ShareReportEventsController
    );
    public isMobile = this.deviceService.getIsMobile();
    private contacts: GetUsersContactControllerType = null;
    public showLeftSide: boolean = true;
    public iconButton: "fas fa-chevron-right" | "fas fa-chevron-left" =
        "fas fa-chevron-left";
    public device: DevicesType = "desktop";
    public report: ReportEntity;
    public company: CompanyEntity;
    public accordionState: string = null;
    public reportType: string = "";
    public loadingScroll: boolean = false;

    public set loading(value: boolean) {
        this.loadingSignal.set(value);
    }
    public get loading() {
        return this.loadingSignal();
    }
    private readonly loadingSignal = signal<boolean>(false);
    public isPreviewMode: boolean = true;
    public isMultiSource: boolean = false;

    @ViewChild(ReportDashboardComponent)
    private reportDashboardComponent: ReportDashboardComponent;

    constructor() {
        effect(async () => {
            const loading = this.loadingSignal();
            if (!loading && this.report?.props) {
                this.onOpenNewsPreviewDashboard();
                this.getContacts();
                this.isMultiSource = this.report?.sources?.length > 1;
            }
        });

        effect(() => {
            const isOpen = this.chatNandaService.isOpen;
            if (isOpen) {
                this.showLeftSide = false;
                this.iconButton = this.showLeftSide
                    ? "fas fa-chevron-left"
                    : "fas fa-chevron-right";
            }
        });
    }

    async ngOnInit(): Promise<void> {
        const { reportId } = this.route.snapshot.queryParams;
        const report = await this.getReportController.execute({
            reportId: reportId
        });
        this.report = report;

        this.reportType = report.type;
    }

    public onToggleNavigation(): void {
        this.showLeftSide = !this.showLeftSide;
        this.iconButton = this.showLeftSide
            ? "fas fa-chevron-left"
            : "fas fa-chevron-right";
    }

    public onSelectDevice(device: DevicesType): void {
        this.device = device;
        this.deviceService.forceIsMobile(device === "mobile");
    }

    public onChangeReport(event: EventEmitterReportType): void {
        this.report = event.report;
        this.company = event.company;
        this.loadingSignal.set(event.loading);
    }

    public onOpenNewsPreviewDashboard() {
        const user = this.contactService.get();
        if (
            user.props?.featureFlag?.newsPreviewDashboard ||
            user.props?._id === "convidadoId"
        )
            return;
        const dialogNewsPreviewDashboard = this.dialog.open(
            DashNewsDialogComponent,
            {
                width: "100%",
                maxWidth: "876px",
                height: "auto",
                maxHeight: "auto",
                panelClass: "share-email-dialog-component-parent",

                data: {}
            }
        );
        dialogNewsPreviewDashboard
            .afterClosed()
            .subscribe(async (result: any) => {
                await this.updateFeatureFlagUserController.execute({
                    accountingId: this.report?.props?.accountingId,
                    user: user,
                    featureFlag: "newsPreviewDashboard"
                });
            });
    }

    public onOpenDialogEmail(): void {
        const dialogShareEmail = this.dialog.open(ShareEmailDialogComponent, {
            width: "100%",
            maxWidth: "672px",
            height: "100%",
            maxHeight: "507px",
            panelClass: "share-email-dialog-component-parent",

            data: {
                handleListContacts: async () => await this.getContacts(),
                contacts: this.contacts,
                companyId: this.report?.props?.companyId,
                company: this.company,
                accountingId: this.report?.props?.accountingId,
                report: this.report
            }
        });
        dialogShareEmail.afterClosed().subscribe(async (result: any) => {
            if (result) {
                this.shareReportContacts(result, "email", "contact");
                const { SnackbarComponent } = await import(
                    "@app/shared/components/dsn/snackbar/snackbar.component"
                );
                this.snackBar.openFromComponent(SnackbarComponent, {
                    data: {
                        message: "Dash enviado com sucesso!"
                    },
                    duration: 10000
                });
            }
        });
    }

    public onOpenDialogWpp(): void {
        const dialogShareWpp = this.dialog.open(ShareWppDialogComponent, {
            width: "100%",
            maxWidth: "672px",
            height: "100%",
            maxHeight: "507px",
            panelClass: "share-email-dialog-component-parent",
            data: {
                contacts: this.contacts,
                companyId: this.report?.props?.companyId,
                accountingId: this.report?.props?.accountingId,
                report: this.report,
                handleListContacts: async () => await this.getContacts()
            }
        });
        dialogShareWpp.afterClosed().subscribe(async (result: any) => {
            if (result) {
                this.shareReportContacts(result, "whatsapp", "contact");

                if (!this.company?.props?.accounting?.whatsapp) {
                    this.editWhatsappDialog();
                }
                const { SnackbarComponent } = await import(
                    "@app/shared/components/dsn/snackbar/snackbar.component"
                );
                this.snackBar.openFromComponent(SnackbarComponent, {
                    data: {
                        message: "Dash enviado com sucesso!"
                    },
                    duration: 10000
                });
            }
        });
    }

    private editWhatsappDialog(): any {
        return this.dialog.open(EditWhatsappDialogComponent, {
            width: "100%",
            maxWidth: "524px",
            height: "auto",
            data: {
                title: "WhatsApp da contabilidade",
                whatsapp: "",
                showMessage: false,
                company: this.company
            }
        });
    }

    private async shareReportContacts(
        result: any,
        sharingType: "whatsapp" | "email",
        type: "contact" | "user"
    ): Promise<void> {
        const modelContacts: any = (c: any) =>
            c.map((c: any) => ({
                _id: c.props._id,
                name: c.props.name,
                ...(c.props.whatsapp && { whatsapp: c.props.whatsapp }),
                email: c.props?.email || c.props?.username,
                type
            }));
        const contactsSelected = result?.contacts.map((c: any) => c._id);
        const emailChips = result.chips.map((c: string) => {
            const name = c.replace(/@.*/, "");
            return { [sharingType]: c, name };
        });
        const usersContacts = modelContacts(
            this.contacts?.usersContact.filter((c) =>
                contactsSelected.includes(c.props._id)
            )
        );
        const companyContacts = modelContacts(
            this.contacts?.companyContacts.filter((c) =>
                contactsSelected.includes(c.props._id)
            )
        );
        const user = this.contactService.get();
        await this.shareReportController.execute({
            optionsSharing: sharingType,
            accountingId: this.report?.props?.accountingId,
            userId: user.props._id,
            reportId: this.report?.props?._id,
            companyId: this.company.props._id,
            contacts: [...usersContacts, ...companyContacts, ...emailChips]
        });
    }

    public onChangeAccordion(value: string): void {
        this.accordionState = value;
    }

    private async getContacts() {
        const user = this.contactService.get();
        const response = await this.getUsersContactController.execute({
            report: this.report,
            company: this.company,
            user
        });
        this.contacts = response;
        return response;
    }

    public onClickOldReport(): void {
        this.eventsService.send(
            `Dash.${this.report.type}.${this.report.humanSourceName}.acessar.relatorio.antigo`
        );
    }

    public async onCopyLink(): Promise<void> {
        this.eventsService.send(
            `Dash.${this.report.type}.${this.report.humanSourceName}.acessar-report-v1`
        );
        const types: Record<string, string> = {
            dashboard: "dashboard",
            dre: "dre",
            dreComparation: "dre",
            balance: "balanco",
            balanceComparation: "balanco",
            budget: "orcamento",
            projection: "projecao"
        };
        if (!types[this.report.type]) return;
        const baseUrl = window.location.origin;
        navigator.clipboard.writeText(
            `${baseUrl}/${types[this.report.type]}?companyId=${this.company.props._id}&reportId=${this.report.props._id}&liveToken=${environment.LIVE_TOKEN}`
        );
        const { SnackbarComponent } = await import(
            "@app/shared/components/dsn/snackbar/snackbar.component"
        );
        this.snackBar.openFromComponent(SnackbarComponent, {
            data: {
                message: "Link copiado para área de transferência"
            },
            duration: 10000
        });
        this.shareReportEvents({
            shareType: "link",
            eventType: "reportShared",
            shared: "USER_LINK"
        });
    }

    private async shareReportEvents({
        shareType,
        eventType,
        shared
    }: {
        shareType: string;
        eventType: string;
        shared: string;
    }): Promise<void> {
        const contact = this.contactService.get();
        await this.shareReportEventsController.execute({
            report: this.report,
            contact,
            shareType,
            eventType,
            shared
        });
    }

    public async onOpenDashboard(): Promise<void> {
        const user = this.contactService.get();
        const types: Record<string, string> = {
            dashboard: "dashboard",
            dre: "dre",
            dreComparation: "dre",
            balance: "balanco",
            balanceComparation: "balanco"
        };
        if (!types[this.report.type]) return;
        const baseUrl = window.location.origin;
        window.open(
            `${baseUrl}/${types[this.report.type]}?companyId=${this.company.props._id}&reportId=${this.report.props._id}&liveToken=${environment.LIVE_TOKEN}&userId=${user?.props?._id}`,
            "_blank"
        );
        this.eventsService.send(
            `Dash.${this.report.type}.${this.report.humanSourceName}.acessar-report-v1`
        );
    }

    public getOldUrlReport(): string {
        if (!this.report) return "#";
        if (this.loading) return "#";
        return this.report.getOldUrl();
    }

    public onDownload(): void {
        const url = this.report.getPDFUrl();
        if (!url) return;
        window.open(url, "_blank");
        this.eventsService.send(
            `Dash.${this.report.type}.${this.report.humanSourceName}.gerar-pdf`
        );
        this.shareReportEvents({
            shareType: "donwload",
            eventType: "reportDownloaded",
            shared: "USER_DOWNLOAD"
        });
    }

    onOpenChat() {
        if (!this.accountingPlanService.features?.["IAContador"]) {
            this.requestNandaIA();
            return;
        }
        this.chatNandaService.open();
        this.showLeftSide = false;
        this.iconButton = this.showLeftSide
            ? "fas fa-chevron-left"
            : "fas fa-chevron-right";
    }

    requestNandaIA() {
        this.dialog.open(RequestProductGrowthComponent, {
            width: "386px",
            maxWidth: "386px",
            data: {
                title: "Nanda IA",
                imageSrc: "images/nanda-ia-banner.png",
                subtitle: "Nanda IA integrada!",
                description:
                    "O Chat da Nanda IA permite conversar diretamente no <span class='font-weight-semi-bold'>Dashboard, Dash e Showtime</span>, gerando insights e esclarecendo dúvidas de forma rápida.",
                hint: "Essa funcionalidade <span class='font-weight-semi-bold'>não está disponível</span> no seu plano atual. Solicite o contato abaixo para incluir a Nanda IA no seu plano.",
                cta: "Solicitar Nanda IA",
                company: this.company,
                package: "Chat da IA do Contador",
                licenses: 1,
                options: {
                    addedValue: "0",
                    period: ""
                }
            }
        });
    }

    onCloseChat() {
        this.showLeftSide = true;
        this.iconButton = this.showLeftSide
            ? "fas fa-chevron-left"
            : "fas fa-chevron-right";
    }

    public onChangeVisualizationsVisible(): void {
        if (this.reportType === "dashboard" && this.reportDashboardComponent) {
            this.reportDashboardComponent.onChangeVisualizationsVisible();
        }
    }
}

type DevicesType = "desktop" | "mobile";
type EventEmitterReportType = {
    loading: boolean;
    company?: CompanyEntity;
    report: ReportEntity;
};
